import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';

import { AuthProvider, useAuth } from './AuthContext';
import { ToastProvider } from './ToastContext';
import SellerDealList  from './pages/seller/DealList';
import DealEditor      from './pages/seller/DealEditor';
import SellerTransactions from './pages/seller/Transactions';
import SellerReports   from './pages/seller/Reports';
import BuyerDealList   from './pages/buyer/DealList';
import DealView        from './pages/buyer/DealView';
import BuyerPending    from './pages/buyer/Pending';
import Setup          from './pages/Setup';
import Settings       from './pages/Settings';
import Upgrade        from './pages/Upgrade';
import ErrorBoundary        from './components/ErrorBoundary';
import NotificationBell    from './components/NotificationBell';
import Admin from './pages/admin/Admin';
import { Terms, Privacy } from './pages/Legal';
import Landing from './pages/Landing';
import Tutorial, { TUTORIAL_KEY } from './components/Tutorial';

const LAST_MODE_KEY = 'lanebids-last-mode';

function Header({ darkMode, setDarkMode, onHelp }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { appUser, logout } = useAuth();
  const subExpiry = appUser?.account?.subscription_expiration;
  const hasSubscription = appUser?.account?.subscription_tier != null
    && (!subExpiry || new Date(subExpiry) > new Date());

  // Remember which side (seller/buyer) the user was last actively viewing, so
  // navigating to a shared route like /admin doesn't flip the nav to buyer mode.
  // Persisted to localStorage (U1) so the root route can default a subscribed
  // account's next login to whichever side they last used, not always buyer.
  const [lastMode, setLastMode] = useState(() =>
    pathname.startsWith('/seller') ? 'seller'
    : pathname.startsWith('/buyer') ? 'buyer'
    : localStorage.getItem(LAST_MODE_KEY) || 'buyer'
  );
  useEffect(() => {
    if (pathname.startsWith('/seller')) { setLastMode('seller'); localStorage.setItem(LAST_MODE_KEY, 'seller'); }
    else if (pathname.startsWith('/buyer')) { setLastMode('buyer'); localStorage.setItem(LAST_MODE_KEY, 'buyer'); }
  }, [pathname]);

  const isSeller = hasSubscription && lastMode === 'seller';
  const isPlatformAdmin = appUser?.is_platform_admin;

  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  const navLinks = isSeller
    ? [
        { to: '/seller/deals', label: 'Deals' },
        { to: '/seller/transactions', label: 'Sales' },
        { to: '/seller/reports', label: 'Reports' },
        { to: '/seller/settings', label: appUser?.is_admin ? 'Account' : 'Settings' },
      ]
    : [
        { to: '/buyer/deals', label: 'Available Deals' },
        { to: '/buyer/pending', label: 'Purchases' },
        { to: '/settings', label: appUser?.is_admin ? 'Account' : 'Settings' },
      ];
  if (!isSeller && appUser?.is_admin && !hasSubscription) navLinks.push({ to: '/upgrade', label: 'Upgrade' });
  if (isPlatformAdmin) navLinks.push({ to: '/admin', label: 'Admin' });

  const NavLink = ({ to, children }) => (
    <Link
      to={to}
      className={`text-sm font-medium px-1 py-0.5 transition-colors ${
        pathname.startsWith(to) ? 'text-white' : 'text-slate-400 hover:text-slate-200'
      }`}
    >
      {children}
    </Link>
  );

  return (
    <header className="relative bg-slate-900 border-b border-slate-800 px-3 md:px-6 h-12 flex items-center justify-between shrink-0 z-10">
      <div className="flex items-center gap-4 md:gap-8 min-w-0">
        <img src="/logo-white.svg" alt="Lane Bids" className="h-6 md:h-7 shrink-0" />
        <nav className="hidden md:flex items-center gap-5">
          {navLinks.map(l => <NavLink key={l.to} to={l.to}>{l.label}</NavLink>)}
        </nav>
      </div>

      <div className="flex items-center gap-1.5 md:gap-2">
        {appUser && <NotificationBell />}
        <button
          onClick={() => onHelp(isSeller ? 'seller' : 'buyer')}
          aria-label="Replay tutorial"
          title="Replay tutorial"
          className="hidden md:flex items-center justify-center w-7 h-7 text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded border border-slate-700 hover:border-slate-600 transition-colors text-xs font-semibold"
        >
          ?
        </button>
        <button
          onClick={() => setDarkMode(d => !d)}
          className="hidden md:flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded border border-slate-700 hover:border-slate-600 transition-colors"
        >
          {darkMode ? (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              Light
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
              Dark
            </>
          )}
        </button>

        {appUser && (
          <span className="text-xs text-slate-500 hidden md:block">
            {appUser.fname ? `${appUser.fname} ${appUser.lname}`.trim() : appUser.email}
          </span>
        )}

        {hasSubscription && (
          <div className="flex items-center gap-0.5 bg-slate-800 rounded p-0.5 border border-slate-700">
            {['Seller', 'Buyer'].map((r) => (
              <button
                key={r}
                onClick={() => navigate(r === 'Seller' ? '/seller/deals' : '/buyer/deals')}
                className={`px-3 py-1 min-h-[38px] sm:min-h-0 rounded text-xs font-semibold transition-all ${
                  (r === 'Seller') === isSeller
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {r}
              </button>
            ))}
          </div>
        )}

        {!hasSubscription && (
          <div className="flex items-center gap-0.5 bg-slate-800 rounded p-0.5 border border-slate-700">
            <button className="px-3 py-1 min-h-[38px] sm:min-h-0 rounded text-xs font-semibold bg-blue-600 text-white">Buyer</button>
          </div>
        )}

        <button
          onClick={logout}
          className="hidden md:block text-xs text-slate-500 hover:text-slate-300 transition-colors px-1"
        >
          Sign out
        </button>

        <button
          onClick={() => setMenuOpen(o => !o)}
          aria-label="Menu"
          aria-expanded={menuOpen}
          className="md:hidden flex items-center justify-center w-9 h-9 -mr-1 text-slate-400 hover:text-slate-200 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            {menuOpen ? (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            ) : (
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            )}
          </svg>
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden absolute top-full inset-x-0 bg-slate-900 border-b border-slate-800 shadow-lg z-20">
          <nav className="flex flex-col px-3 py-2">
            {navLinks.map(l => (
              <Link
                key={l.to}
                to={l.to}
                className={`px-3 py-2.5 rounded text-sm font-medium transition-colors ${
                  pathname.startsWith(l.to) ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                {l.label}
              </Link>
            ))}
          </nav>
          <div className="border-t border-slate-800 px-4 py-3 flex items-center justify-between gap-3">
            <button
              onClick={() => setDarkMode(d => !d)}
              className="shrink-0 whitespace-nowrap flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded border border-slate-700 hover:border-slate-600 transition-colors"
            >
              {darkMode ? 'Light mode' : 'Dark mode'}
            </button>
            <button
              onClick={() => onHelp(isSeller ? 'seller' : 'buyer')}
              className="shrink-0 px-2.5 py-1.5 text-xs font-medium text-slate-400 hover:text-slate-100 hover:bg-slate-800 rounded border border-slate-700 hover:border-slate-600 transition-colors"
            >
              Tutorial
            </button>
            {appUser && (
              <span className="min-w-0 flex-1 text-xs text-slate-500 truncate">
                {appUser.fname ? `${appUser.fname} ${appUser.lname}`.trim() : appUser.email}
              </span>
            )}
            <button
              onClick={logout}
              className="text-xs text-slate-500 hover:text-slate-300 transition-colors px-1 shrink-0"
            >
              Sign out
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

// U5: differentiate *why* account load failed so the user knows whether to
// sign in again or just wait and retry, instead of one generic message.
const AUTH_ERROR_COPY = {
  session: { message: 'Your session is no longer valid.', action: 'signin' },
  network: { message: "Can't reach the server. Check your connection.", action: 'retry' },
  server:  { message: 'The server is having trouble right now.', action: 'retry' },
};

function AppShell() {
  const { ready, appUser, authError, logout } = useAuth();
  const { pathname } = useLocation();
  const [darkMode, setDarkMode] = useState(() => {
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) return stored === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // First-run tutorial: fires the first time each mode (buyer/seller) is viewed
  // on this device, once the user is past Setup. Replayable from the header's
  // ? button. Seen-state is per-device (localStorage) by design for now.
  // The seen-flag is written as soon as the tour is shown, not on completion —
  // the overlay's backdrop lets clicks reach the app underneath, so a user can
  // navigate away without ever hitting Close/Done, and if the flag only landed
  // on completion the tour would restart from slide 1 on every future visit.
  const [tutorial, setTutorial] = useState(null); // 'buyer' | 'seller' | null
  useEffect(() => {
    if (tutorial || !appUser?.location_id) return;
    const subExpiry = appUser?.account?.subscription_expiration;
    const subscribed = appUser?.account?.subscription_tier != null
      && (!subExpiry || new Date(subExpiry) > new Date());
    const mode = pathname.startsWith('/seller') && subscribed ? 'seller'
      : pathname.startsWith('/buyer') ? 'buyer' : null;
    if (mode && !localStorage.getItem(TUTORIAL_KEY(mode))) {
      localStorage.setItem(TUTORIAL_KEY(mode), '1');
      setTutorial(mode);
    }
  }, [pathname, appUser, tutorial]);

  // Public documents — readable without signing in, so they sit above the
  // ready/appUser gates.
  if (pathname === '/terms')   return <Terms />;
  if (pathname === '/privacy') return <Privacy />;

  if (!ready) {
    return (
      <div className="flex items-center justify-center h-dvh bg-slate-50 dark:bg-slate-900">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Signed-out visitors at the root get the marketing page rather than a login
  // redirect. Anyone already signed in falls through to the "/" route below,
  // which forwards them into the app — nobody who has an account has to look at
  // the pitch. An auth error still wins, so a broken session is not disguised
  // as a logged-out visit.
  if (!appUser && !authError && pathname === '/') return <Landing />;

  if (!appUser) {
    const copy = AUTH_ERROR_COPY[authError?.kind] || { message: 'Unable to load your account.', action: 'retry' };
    return (
      <div className="flex flex-col items-center justify-center h-dvh gap-3 bg-slate-50 dark:bg-slate-900">
        <p className="text-sm text-slate-500 dark:text-slate-400">{copy.message} Please try again.</p>
        {copy.action === 'signin' ? (
          <button onClick={logout} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">Sign in again</button>
        ) : (
          <button onClick={() => window.location.reload()} className="text-sm text-blue-600 dark:text-blue-400 hover:underline">Retry</button>
        )}
      </div>
    );
  }

  if (!appUser.location_id) {
    return <Setup />;
  }

  const subExpiryShell = appUser?.account?.subscription_expiration;
  const hasSubscription = appUser?.account?.subscription_tier != null
    && (!subExpiryShell || new Date(subExpiryShell) > new Date());

  if (!hasSubscription && pathname.startsWith('/seller')) {
    return <Navigate to="/buyer/deals" replace />;
  }

  if (pathname.startsWith('/admin') && !appUser?.is_platform_admin) {
    return <Navigate to="/buyer/deals" replace />;
  }

  // U1: a subscribed account's first-ever login (no stored preference yet) defaults
  // to the Seller view; subsequent logins go to whichever side they last actively used.
  const storedMode = localStorage.getItem(LAST_MODE_KEY);
  const rootTarget = hasSubscription && storedMode !== 'buyer' ? '/seller/deals' : '/buyer/deals';

  return (
    <div className="flex flex-col h-dvh overflow-x-hidden bg-slate-50 dark:bg-slate-900 font-sans transition-colors">
      <Header darkMode={darkMode} setDarkMode={setDarkMode} onHelp={setTutorial} />
      {tutorial && <Tutorial mode={tutorial} onClose={() => setTutorial(null)} />}
      <main className="flex-1 overflow-auto">
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<Navigate to={rootTarget} replace />} />
            <Route path="/seller/deals"     element={hasSubscription ? <SellerDealList /> : <Navigate to="/buyer/deals" replace />} />
            <Route path="/seller/deals/new" element={hasSubscription ? <DealEditor />     : <Navigate to="/buyer/deals" replace />} />
            <Route path="/seller/deals/:id" element={hasSubscription ? <DealEditor />     : <Navigate to="/buyer/deals" replace />} />
            <Route path="/seller/transactions" element={hasSubscription ? <SellerTransactions /> : <Navigate to="/buyer/deals" replace />} />
            <Route path="/seller/reports"   element={hasSubscription ? <SellerReports />     : <Navigate to="/buyer/deals" replace />} />
            <Route path="/seller/pending"     element={<Navigate to="/seller/transactions" replace />} />
            <Route path="/seller/bids"        element={<Navigate to="/seller/transactions" replace />} />
            <Route path="/seller/settings"  element={hasSubscription ? <Settings />       : <Navigate to="/buyer/deals" replace />} />
            <Route path="/buyer/deals"      element={<BuyerDealList />} />
            <Route path="/buyer/deals/:id"  element={<DealView />} />
            <Route path="/buyer/pending"    element={<BuyerPending />} />
            <Route path="/settings"         element={<Settings />} />
            <Route path="/upgrade"          element={<Upgrade />} />
            <Route path="/admin"            element={<Admin />} />
            <Route path="*"                 element={<Navigate to="/" replace />} />
          </Routes>
        </ErrorBoundary>
      </main>
      <footer className="shrink-0 px-4 text-center text-[11px] text-slate-400 dark:text-slate-600 bg-slate-50 dark:bg-slate-900">
        {/* Padding on the links rather than the footer, so each one carries its own
            comfortable touch area without growing the bar on desktop. */}
        <Link to="/terms" className="inline-flex items-center min-h-[44px] px-2 hover:underline">Terms</Link>
        <span className="mx-0.5">·</span>
        <Link to="/privacy" className="inline-flex items-center min-h-[44px] px-2 hover:underline">Privacy</Link>
      </footer>
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppShell />
        </BrowserRouter>
      </AuthProvider>
    </ToastProvider>
  );
}
